import * as React from 'react';
import { useField } from 'formik';
import { Input as RSInput, Label } from 'reactstrap';
import {
  FormItem,
  FormItemProps,
  Input as UIKitInput,
  InputProps as UIKitInputProps,
  Typography,
} from '@rtkwlf/ui';
import InputDropdown, { Props as InputDropdownProps } from './InputDropdown';
import { OptionType } from '../types';
import { ValueType } from 'react-select/src/types';
import { DARK_PLACEHOLDER, getNewValue, SELECT_STYLE } from '../constants';
import styled from 'styled-components';

interface FormikInputProps
  extends Pick<
    UIKitInputProps,
    | 'htmlType'
    | 'id'
    | 'className'
    | 'label'
    | 'required'
    | 'disabled'
    | 'description'
    | 'size'
  > {
  name: string;
  warningFunc?: (value: string) => string;
}

const StyledLabel = styled(Label)`
  font-size: 14px;
  font-weight: 600;
  color: #000000;
`;

const FormikInput: React.FunctionComponent<FormikInputProps> = (props) => {
  const { name, required, warningFunc } = props;
  const [field, meta] = useField(name);
  const { onBlur, onChange, value } = field;
  const { touched, error } = meta;

  // props from the Formik context
  const propsFromFormik: UIKitInputProps = { value, onBlur, onChange };

  const validationStatus: FormItemProps = {};

  // Set validation if the field is required
  // Or if the field isn't required, only validate when it isn't empty
  if (touched && (required || value !== '')) {
    const warning = warningFunc ? warningFunc(value) : '';
    validationStatus.status = error ? 'error' : warning ? 'warning' : 'success';
    validationStatus.message = error || warning;
  }

  return (
    <FormItem {...validationStatus} hasIcon={false}>
      <UIKitInput {...props} {...propsFromFormik} />
    </FormItem>
  );
};

type InputProps = Omit<FormikInputProps, 'htmlType'>;

const TextInput: React.FunctionComponent<InputProps> = (props) => {
  return <FormikInput htmlType="text" {...props} />;
};

const NumberInput: React.FunctionComponent<InputProps> = (props) => {
  return <FormikInput htmlType="number" {...props} />;
};

const TextArea: React.FunctionComponent<InputProps> = (props) => {
  return <FormikInput htmlType="textarea" size="large" {...props} />;
};

type V2Props = InputDropdownProps<OptionType> & {
  options: OptionType[];
  name: string;
  label?: string;
  required?: boolean;
  id?: string;
};

/**
 * Similar functionality to FormikInputDropdown; implemented with hooks; single select only.
 * Having no selection yields a field value of "" on the form.
 * Usage: directly i.e. <SingleSelect />; do not use Formik.Field
 */
const SingleSelect: React.FunctionComponent<V2Props> = (props) => {
  const { name, label, required, className, id, ...rest } = props;
  const [{ value }, { error }, { setValue }] = useField<string>(name);

  const [touched, setTouched] = React.useState(false);
  const onChange = React.useCallback(
    (value: ValueType<OptionType>) => {
      const v = getNewValue(value, false);
      // submits the value to formik instead of a full OptionType object
      setValue(v ? v.value : '');
    },
    [setValue],
  );

  // find the OptionType object using formik's value
  const selected = React.useMemo(
    // null forces placeholder to be present on empty value
    () => props.options.find((option) => option.value === value) ?? null,
    [props.options, value],
  );
  //for={name}
  return (
    <div className={className}>
      {label && (
        <StyledLabel className={required ? 'required' : ''}>
          {label}
        </StyledLabel>
      )}
      <InputDropdown
        isCreatable={false}
        noOptionsMessage={() => 'No suggestions'}
        showDropdownArrow={true}
        onChange={onChange}
        styles={props.isDisabled ? SELECT_STYLE : DARK_PLACEHOLDER}
        value={selected}
        onFocus={() => setTouched(true)}
        classNamePrefix={id}
        id={id}
        {...rest}
      />
      {error && touched && (
        <label className="react-select-error-label">{error}</label>
      )}
    </div>
  );
};

/**
 * Similar functionality to FormikInputDropdown; implemented with hooks; multi select only.
 * Having no selection yields a field value of [] on the form.
 * Usage: directly i.e. <MultiSelect />; do not use Formik.Field
 */
const MultiSelect: React.FunctionComponent<V2Props> = (props) => {
  const { name, label, required, className, id, ...rest } = props;
  const [{ value }, { error }, { setValue }] = useField<string[]>(name);

  const [touched, setTouched] = React.useState(false);
  const onChange = React.useCallback(
    (value: ValueType<OptionType>) => {
      const v = getNewValue(value, true);
      // submits the value list to formik instead of the OptionType list
      setValue((v || []).map((v) => v.value));
    },
    [setValue],
  );

  // find the OptionType objects using formik's value list
  const selected = React.useMemo(() => {
    const availableOptionsSet = new Set();
    const selections = props.options.filter((option) => {
      availableOptionsSet.add(option.value);
      return value.includes(option.value);
    });
    // Push the selected options that are no longer available to selections.
    for (let val of value) {
      if (!availableOptionsSet.has(val)) {
        const option = { label: val, value: val };
        selections.push(option);
      }
    }
    return selections.length > 0 ? selections : null;
  }, [props.options, value]);

  return (
    <div className={className}>
      {label && (
        <Label className={required ? 'required' : ''} for={name}>
          {label}
        </Label>
      )}
      <InputDropdown
        isMulti={true}
        isCreatable={false}
        noOptionsMessage={() => 'No suggestions'}
        showDropdownArrow={true}
        onChange={onChange}
        styles={props.isDisabled ? SELECT_STYLE : DARK_PLACEHOLDER}
        value={selected}
        onFocus={() => setTouched(true)}
        classNamePrefix={id}
        id={id}
        {...rest}
      />
      {error && touched && (
        <label className="react-select-error-label">{error}</label>
      )}
    </div>
  );
};

type CheckboxProps = {
  name: string;
  className?: string;
  id?: string;
  label?: React.ReactNode;
  disabled?: boolean;
  onClick?: (event: React.SyntheticEvent<HTMLInputElement>) => any;
  onChange?: (event: React.SyntheticEvent<HTMLInputElement>) => any;
};

const Checkbox: React.FunctionComponent<CheckboxProps> = (props) => {
  const { name, label, className, disabled, onClick, ...rest } = props;
  const [field] = useField(name);

  const { onChange } = field;
  return (
    <Label check className={className}>
      <RSInput
        checked={field.value}
        name={name}
        disabled={disabled}
        onChange={onChange}
        {...rest}
        onClick={onClick}
        type="checkbox"
      />
      {label}
    </Label>
  );
};

type RadioButtonProps<V> = {
  name: string;
  option: { label: React.ReactNode; value: V };
  disabled?: boolean;
  onChangeCallback?: (name: string, value: V) => void;
  checkedValue?: V;
};

const RadioButton = <V extends {}>({
  name,
  option,
  disabled,
  onChangeCallback,
  checkedValue,
}: RadioButtonProps<V>) => {
  const [{ value }, , { setValue }] = useField<V>(name);

  const onChange = React.useCallback(() => {
    setValue(option.value);
    if (onChangeCallback) {
      onChangeCallback(name, option.value);
    }
  }, [name, option.value, setValue, onChangeCallback]);

  return (
    <Label className="pl-4" check>
      <RSInput
        checked={value === option.value || option.value === checkedValue}
        onChange={onChange}
        type="radio"
        name={name}
        value={`${option.value}`}
        disabled={disabled}
      />
      {option.label}
    </Label>
  );
};

type RadioButtonsProps<V> = {
  name: string;
  label?: React.ReactNode;
  className?: string;
  options: {
    label: React.ReactNode;
    value: V;
    customElement?: React.ReactNode;
  }[];
  disabledOptions?: V[];
  onChangeCallback?: (name: string, value: V) => void;
  checkedValue?: V;
};

const RadioButtons = <V extends {}>({
  name,
  label,
  className,
  options,
  disabledOptions,
  onChangeCallback,
  checkedValue,
}: RadioButtonsProps<V>) => {
  const [, { touched, error }] = useField(name);

  return (
    <div className={className}>
      {label && <Typography.Label>{label}</Typography.Label>}
      {options.map((option) => (
        <div key={`${option.value}`} className="d-flex align-items-center">
          <RadioButton
            key={`${option.value}`}
            name={name}
            option={option}
            disabled={disabledOptions?.includes(option.value)}
            onChangeCallback={() => {
              if (onChangeCallback) {
                onChangeCallback(name, option.value);
              }
            }}
            checkedValue={checkedValue}
          />
          {option.customElement}
        </div>
      ))}
      {error && touched && (
        <label className="react-select-error-label">{error}</label>
      )}
    </div>
  );
};

export {
  TextInput,
  TextArea,
  SingleSelect,
  MultiSelect,
  NumberInput,
  Checkbox,
  RadioButtons,
};
